import type { TenantSlug } from "@/lib/theme-presets";

/**
 * The People drawing's visit fixtures (references/03-people, scene b851208c2dbb,
 * merged in A v2.4). Specimen data only: no email, no phone. The teacher and
 * the student share ids across tenants so each state visits one address.
 *
 * C3: every refused and not-confirmed sentence is a placeholder for the
 * server's own sentence.
 */

export type InvitationState = { word: string; class: "waiting" | "quiet" } | null;

export interface SpecimenPerson {
  id: string;
  name: string;
  sid: string | null;
  rel: "Faculty" | "Student" | "Admin";
  account: "Active" | "No LMS account";
  invitation: InvitationState;
}

export interface TenantPeople {
  program: string;
  term: string;
  courses: [string, string];
  /** Who owns marks and attendance: IMAM, or Google Classroom on Mufid (ADR 0026). */
  marksOwner: "imam" | "classroom";
  people: SpecimenPerson[];
}

const EXPIRED = { word: "Invitation expired", class: "waiting" } as const;
const NOT_DELIVERED = { word: "Setup email not delivered", class: "waiting" } as const;
const SENT = { word: "Invitation sent", class: "quiet" } as const;

export const PEOPLE: Record<TenantSlug, TenantPeople> = {
  murtada: {
    program: "Teaching Day Foundations",
    term: "Teaching Day Archive Term",
    courses: ["TDAY-101 · Ethics in Practice", "TDAY-120 · Reading Sources"],
    marksOwner: "imam",
    people: [
      { id: "p1", name: "Teaching Day Admin", sid: null, rel: "Admin", account: "Active", invitation: null },
      { id: "p2", name: "Layla Karimi", sid: null, rel: "Faculty", account: "No LMS account", invitation: NOT_DELIVERED },
      { id: "p3", name: "Maryam Teaching Day", sid: "SID 26-0412", rel: "Student", account: "Active", invitation: null },
      { id: "p4", name: "Hasan Kazmi", sid: null, rel: "Faculty", account: "No LMS account", invitation: EXPIRED },
      { id: "p5", name: "Yusuf Naqvi", sid: null, rel: "Faculty", account: "No LMS account", invitation: SENT },
      { id: "p6", name: "Zaynab Hakimi", sid: null, rel: "Faculty", account: "No LMS account", invitation: null },
    ],
  },
  mufid: {
    program: "Mufid Seminary Diploma",
    term: "Fall 2026",
    courses: ["USUL-210 · Uṣūl al-Fiqh: al-Rasāʾil", "FIQH-220 · Comparative jurisprudence"],
    marksOwner: "classroom",
    people: [
      { id: "p1", name: "Zahra Haydari", sid: "SID 26-0187", rel: "Student", account: "No LMS account", invitation: EXPIRED },
      { id: "p2", name: "Ali Mansuri", sid: "SID 26-0191", rel: "Student", account: "No LMS account", invitation: NOT_DELIVERED },
      { id: "p3", name: "Baqir Salih", sid: "SID 26-0203", rel: "Student", account: "Active", invitation: null },
      { id: "p4", name: "Sayyid Muḥammad Ṭabāṭabāʾī", sid: null, rel: "Faculty", account: "Active", invitation: null },
      { id: "p5", name: "Amina Devji", sid: "SID 26-0215", rel: "Student", account: "No LMS account", invitation: SENT },
      { id: "p6", name: "Hadi Moosavi", sid: "SID 26-0222", rel: "Student", account: "No LMS account", invitation: null },
    ],
  },
};

export type ResendOutcome = "none" | "confirmed" | "refused" | "unknown";

/** What a state sets before it draws. The visit itself is the address in the state's URL. */
export interface VisitPreset {
  resend?: ResendOutcome;
  accessFailed?: boolean;
  marksFailed?: boolean;
  /** Boxes opened in place inside the visit, by their kind ("academic", "assign"). */
  expanded?: string[];
  /** The resend went through earlier, so the register row reads Invitation sent. */
  resent?: boolean;
  filter?: "all" | "no-account";
  /** An Invite to sign in draft kept in this browser for p6 (O7). */
  inviteDraft?: boolean;
  /** Add teacher, outside the visit, opened. */
  adding?: boolean;
}

export interface VisitState {
  id: string;
  name: string;
  /** The state's `?visit=`; empty for a state with no visit open. */
  visit: string;
  preset: VisitPreset;
}

/** STATES.md's visit states, plus the second phases the judge needs per address (P4). */
export const VISIT_STATES: readonly VisitState[] = [
  { id: "visit-access", name: "Access", visit: "person:p2/access", preset: {} },
  { id: "visit-access-confirmed", name: "Access, sent", visit: "person:p2/access", preset: { resend: "confirmed" } },
  { id: "visit-access-refused", name: "Access, not sent", visit: "person:p2/access", preset: { resend: "refused" } },
  { id: "visit-access-unknown", name: "Access, not confirmed", visit: "person:p2/access", preset: { resend: "unknown" } },
  { id: "visit-access-failed", name: "Access, read failed", visit: "person:p2/access", preset: { accessFailed: true } },
  { id: "visit-returned", name: "Returned", visit: "", preset: { resent: true, filter: "no-account" } },
  { id: "visit-person", name: "Student", visit: "person:p3", preset: {} },
  { id: "visit-teacher", name: "Teacher", visit: "person:p4", preset: {} },
  { id: "visit-teacher-open", name: "Teacher, assignments open", visit: "person:p4", preset: { expanded: ["assign"] } },
  { id: "visit-onward", name: "Onward", visit: "person:p3,enroll:p3/c1", preset: {} },
  { id: "visit-onward-failed", name: "Onward, read failed", visit: "person:p3,enroll:p3/c1", preset: { marksFailed: true } },
  { id: "visit-onward-returned", name: "Onward, returned", visit: "person:p3", preset: { expanded: ["academic"] } },
  { id: "visit-draft-kept", name: "Draft kept", visit: "", preset: { inviteDraft: true } },
  { id: "visit-replaced", name: "Replaced", visit: "", preset: { inviteDraft: true, adding: true } },
  { id: "visit-refused", name: "Refused", visit: "person:zz", preset: {} },
  { id: "visit-refused-onward", name: "Refused, onward", visit: "person:p3,person:zz", preset: {} },
];

export const READ_AT = "Wed, Oct 21, 6:26 PM";
